import { useEffect, useState } from 'react'
import { api } from '../../shared/api/http'
import { listCompanies, type Company } from '../monitoring/monitoringApi'
import './PortariaAdministration.css'

type ClientSetting = { clientId: string; companyId: string; clientName: string; enabled: boolean }

export function PortariaAdministration() {
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyId, setCompanyId] = useState('')
  const [clients, setClients] = useState<ClientSetting[]>([])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    let active = true
    listCompanies().then(items => {
      if (!active) return
      setCompanies(items)
      setCompanyId(items[0]?.id ?? '')
    }).catch(() => { if (active) setError('Não foi possível carregar as empresas.') })
    return () => { active = false }
  }, [])

  useEffect(() => {
    let active = true
    setClients([])
    setError('')
    if (!companyId) { setLoading(false); return }
    setLoading(true)
    api<ClientSetting[]>(`/portaria/companies/${companyId}/clients`)
      .then(items => { if (active) setClients(items) })
      .catch(() => { if (active) setError('Não foi possível carregar a habilitação de Portaria.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [companyId])

  async function toggle(client: ClientSetting) {
    setBusyId(client.clientId)
    setError('')
    setMessage('')
    try {
      const enabled = !client.enabled
      await api(`/portaria/companies/${companyId}/clients/${client.clientId}`, {
        method: 'PUT', body: JSON.stringify({ enabled }),
      })
      setClients(items => items.map(item => item.clientId === client.clientId
        ? { ...item, enabled } : item))
      setMessage(enabled
        ? `Portaria habilitada para ${client.clientName}. O cadastro será sincronizado automaticamente.`
        : `Portaria desabilitada para ${client.clientName}. O acesso será revogado na próxima sincronização.`)
    } catch {
      setError('Não foi possível alterar a Portaria deste cliente. Tente novamente.')
    } finally { setBusyId(null) }
  }

  return <section className="portaria-admin administration-content">
    <header className="portaria-admin-heading"><span aria-hidden="true">⌂</span><div>
      <small>MÓDULO DE CONDOMÍNIO</small><h2>Spectra Portaria</h2>
      <p>Escolha quais clientes terão a Portaria. Os demais permanecem somente na Vigilância.</p>
    </div></header>
    <label className="portaria-admin-company">Empresa
      <select value={companyId} disabled={!!busyId} onChange={event => setCompanyId(event.target.value)}>
        {companies.map(company => <option key={company.id} value={company.id}>{company.tradeName}</option>)}
      </select>
    </label>
    {message && <p className="portaria-admin-message" role="status">{message}</p>}
    {error && <p className="portaria-admin-error" role="alert">{error}</p>}
    <div className="portaria-admin-list">
      {loading ? <p>Carregando clientes…</p> : clients.length === 0 ? <p>Nenhum cliente cadastrado nesta empresa.</p>
        : clients.map(client => <label key={client.clientId} className="portaria-admin-client">
          <span><strong>{client.clientName}</strong><small>{client.enabled ? 'Portaria habilitada' : 'Somente Vigilância'}</small></span>
          <input type="checkbox" checked={client.enabled} disabled={!!busyId}
            aria-label={`Habilitar Portaria para ${client.clientName}`}
            onChange={() => void toggle(client)} />
        </label>)}
    </div>
    <p className="portaria-admin-note">A Vigilância é a origem dos cadastros de empresa, cliente e usuários. Dados próprios do condomínio ficam no banco separado da Portaria.</p>
  </section>
}
